// Client-side mirror of the MiniMax H3 constants in
// server/src/routes/minimax-h3.ts (the client can't import server code).
// Keep the two in sync; the server re-validates everything on submit.

export const H3_UPLOAD_URL = '/api/v1/minimax-h3/upload';
export const H3_GENERATE_URL = '/api/v1/minimax-h3/generate-async';
export const H3_ENDPOINT = 'minimax-h3';

export type H3Mode = 'text' | 'frames' | 'refs';
export type H3Task = 't2va' | 'fl2va' | 'ref2va';

export const H3_TIERS = ['turbo', 'base'] as const;
export type H3Tier = (typeof H3_TIERS)[number];

export const H3_ASPECT_RATIOS = ['auto', '21:9', '16:9', '4:3', '1:1', '3:4', '9:16'] as const;
export type H3AspectRatio = (typeof H3_ASPECT_RATIOS)[number];
// `auto` only makes sense when there is an input whose aspect can be matched.
export const H3_T2V_ASPECT_RATIOS = H3_ASPECT_RATIOS.filter((a) => a !== 'auto') as Exclude<H3AspectRatio, 'auto'>[];

export const H3_SHORT_EDGES = [480, 768, 1080] as const;
export type H3ShortEdge = (typeof H3_SHORT_EDGES)[number];
export const H3_SHORT_EDGE_LABELS: Record<H3ShortEdge, string> = {
  480: '480p',
  768: '768p',
  1080: '1080p',
};

export const H3_DURATION_MIN = 5;
export const H3_DURATION_MAX = 30;
export const H3_DURATION_DEFAULT = 5;
export const H3_PROMPT_MAX_CHARS = 8000;
export const H3_MAX_REF_IMAGES = 9;
export const H3_MAX_REF_AUDIO = 3;

// Timed keyframes (mirrors server/src/routes/minimax-h3.ts and comfy-pipelet
// minimax_h3/schema.py). H3 renders 24 fps on a 17n+5 frame grid: first frame
// is frame 0, last frame N-1, and a keyframe must land strictly between them.
export const H3_FPS = 24;
export const H3_MAX_ANCHORS = 8; // first + last + interior
export const H3_RECOMMENDED_KEYFRAMES = 3;
export const H3_KEYFRAME_TIME_STEP = 0.1;

export function h3RenderedFrameCount(duration: number): number {
  const n = Math.round(duration * H3_FPS);
  if (n <= 0) return 1;
  return n + ((((5 - n) % 17) + 17) % 17);
}

// Python round() (halves to even), as the worker rounds a time to a frame.
export function h3FrameForTime(time: number): number {
  const x = time * H3_FPS;
  const f = Math.floor(x);
  const d = x - f;
  if (d > 0.5) return f + 1;
  if (d < 0.5) return f;
  return f % 2 === 0 ? f : f + 1;
}

// The latest time (s) a keyframe may sit at, for messages: just before the last frame.
export function h3LastFrameTime(duration: number): number {
  return (h3RenderedFrameCount(duration) - 1) / H3_FPS;
}

export type H3KeyframeIssue =
  | { kind: 'time' }
  | { kind: 'outside' }
  | { kind: 'same'; other: number }; // index of the earlier row on that frame

// Per-row problems with the keyframe times, for a clip of `duration` seconds.
export function keyframeTimeIssues(times: (number | null)[], duration: number): (H3KeyframeIssue | null)[] {
  const last = h3RenderedFrameCount(duration) - 1;
  const seen = new Map<number, number>();
  return times.map((t, i) => {
    if (t == null || !Number.isFinite(t)) return { kind: 'time' };
    const f = h3FrameForTime(t);
    if (t <= 0 || !(f > 0 && f < last)) return { kind: 'outside' };
    if (seen.has(f)) return { kind: 'same', other: seen.get(f)! };
    seen.set(f, i);
    return null;
  });
}

// A sensible default for a new keyframe: the middle of the widest gap between
// the clip's ends and the keyframes already placed, on the 0.1 s grid.
export function nextKeyframeTime(times: number[], duration: number): number {
  const pts = [0, ...times.filter((t) => t > 0 && t < duration).sort((a, b) => a - b), duration];
  let best = 0;
  let at = duration / 2;
  for (let i = 1; i < pts.length; i++) {
    const gap = pts[i] - pts[i - 1];
    if (gap > best) {
      best = gap;
      at = pts[i - 1] + gap / 2;
    }
  }
  return Math.round(at * 10) / 10;
}

export function isH3AspectRatio(v: unknown): v is H3AspectRatio {
  return typeof v === 'string' && (H3_ASPECT_RATIOS as readonly string[]).includes(v);
}
export function isH3ShortEdge(v: unknown): v is H3ShortEdge {
  return typeof v === 'number' && (H3_SHORT_EDGES as readonly number[]).includes(v);
}
export function isH3Tier(v: unknown): v is H3Tier {
  return typeof v === 'string' && (H3_TIERS as readonly string[]).includes(v);
}

// Longest clip offered for a canvas: the engine's 18 megapixel·second budget
// (HD presets render at a 544 base; `auto` follows the square column),
// rounded DOWN to a multiple of 5 s so the slider only shows round stops.
export function maxDurationFor(shortEdge: number, aspect: H3AspectRatio): number {
  const col = aspectColumn(aspect);
  const row = shortEdge >= 1080 ? 544 : shortEdge;
  const table: Record<number, [number, number, number, number]> = {
    480: [30, 30, 30, 30],
    544: [30, 30, 30, 26],
    640: [30, 30, 24, 18],
    704: [30, 27, 20, 15],
    768: [30, 22, 17, 13],
  };
  const keys = Object.keys(table).map(Number).sort((a, b) => a - b);
  const key = keys.find((k) => k >= row) ?? keys[keys.length - 1];
  const raw = Math.min(H3_DURATION_MAX, table[key][col]);
  return Math.max(H3_DURATION_MIN, Math.floor(raw / 5) * 5);
}

function aspectColumn(aspect: H3AspectRatio): 0 | 1 | 2 | 3 {
  switch (aspect) {
    case 'auto':
    case '1:1':
      return 0;
    case '4:3':
    case '3:4':
      return 1;
    case '16:9':
    case '9:16':
      return 2;
    case '21:9':
      return 3;
  }
}

// Retired video queues whose history rows remix onto this page.
export const H3_LEGACY_VIDEO_ENDPOINTS = ['ltx-video', 'wan22-video'] as const;
export function isLegacyVideoEndpoint(endpoint: string | undefined): boolean {
  return !!endpoint && (H3_LEGACY_VIDEO_ENDPOINTS as readonly string[]).includes(endpoint);
}
// Every legacy job re-runs at H3's native canvas, whatever it was rendered at
// (mirrors the server's H3_LEGACY_SHORT_EDGE used by History → Rerun).
export const H3_LEGACY_SHORT_EDGE: H3ShortEdge = 768;
// wan22-video clips were almost all 5 s; they re-run as 8 s videos whatever
// they stored (mirrors the server's H3_WAN22_RERUN_DURATION).
export const H3_WAN22_RERUN_DURATION = 8;

// Router state handed over from a history item's Remix action.
export type H3RemixState = {
  remix?: Record<string, unknown>;
  // Endpoint of the source row; legacy rows get their settings mapped.
  remixFrom?: string;
  // Activity id of the source row, used to re-presign its input r2 keys so
  // the frames / references show up in the tiles again.
  remixActivityId?: number;
};

// Remix settings after mapping. H3 rows come through as-is; legacy
// ltx-video / wan22-video rows carry `min_height` instead of `short_edge` and
// are pinned to 768p (same rules as the server's mapLegacyVideoInputToH3).
export type H3Remix = {
  prompt?: string;
  duration?: number;
  short_edge?: H3ShortEdge;
  aspect_ratio?: H3AspectRatio;
  tier?: H3Tier;
  first_frame_r2_key?: string;
  last_frame_r2_key?: string;
  // Timed keyframes, always as seconds (a stored `frame` is converted).
  keyframes?: { r2_key: string; time: number }[];
  reference_image_r2_keys?: string[];
  reference_video_r2_key?: string;
  reference_audio_r2_keys?: string[];
};

export function normalizeRemix(raw: Record<string, unknown> | null, from?: string): H3Remix | null {
  if (!raw) return null;
  const str = (v: unknown): string | undefined => (typeof v === 'string' && v ? v : undefined);
  const strs = (v: unknown): string[] | undefined => {
    const arr = Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string' && !!x) : [];
    return arr.length ? arr : undefined;
  };
  const out: H3Remix = {
    prompt: str(raw.prompt),
    first_frame_r2_key: str(raw.first_frame_r2_key),
    last_frame_r2_key: str(raw.last_frame_r2_key),
    reference_image_r2_keys: strs(raw.reference_image_r2_keys),
    reference_video_r2_key: str(raw.reference_video_r2_key),
    reference_audio_r2_keys: strs(raw.reference_audio_r2_keys),
    tier: isH3Tier(raw.tier) ? raw.tier : 'turbo',
  };
  const hasFrame = !!out.first_frame_r2_key || !!out.last_frame_r2_key || !!rawKeyframes(raw.keyframes).length;
  if (isLegacyVideoEndpoint(from)) {
    out.short_edge = H3_LEGACY_SHORT_EDGE;
  } else if (isH3ShortEdge(raw.short_edge)) {
    out.short_edge = raw.short_edge;
  } else if (typeof raw.min_height === 'number') {
    out.short_edge = raw.min_height >= 1080 ? 1080 : raw.min_height >= 720 ? 768 : 480;
  }
  if (isH3AspectRatio(raw.aspect_ratio) && !(raw.aspect_ratio === 'auto' && !hasFrame)) {
    out.aspect_ratio = raw.aspect_ratio;
  } else {
    out.aspect_ratio = hasFrame ? 'auto' : '16:9';
  }
  const rawDuration = from === 'wan22-video' ? H3_WAN22_RERUN_DURATION : raw.duration;
  if (typeof rawDuration === 'number' && Number.isFinite(rawDuration)) {
    const cap = maxDurationFor(out.short_edge ?? 768, out.aspect_ratio);
    out.duration = Math.min(cap, Math.max(H3_DURATION_MIN, Math.round(rawDuration)));
  }
  const kfs = rawKeyframes(raw.keyframes);
  if (kfs.length) {
    // A public-API job may store a frame index; the page edits seconds.
    const n = h3RenderedFrameCount(typeof raw.duration === 'number' ? raw.duration : out.duration ?? H3_DURATION_DEFAULT);
    out.keyframes = kfs.map((k) => ({
      r2_key: k.r2_key,
      time: k.time ?? Math.round((((k.frame ?? 0) < 0 ? (k.frame ?? 0) + n : k.frame ?? 0) / H3_FPS) * 1000) / 1000,
    }));
  }
  return out;
}

function rawKeyframes(v: unknown): { r2_key: string; time?: number; frame?: number }[] {
  if (!Array.isArray(v)) return [];
  return v.flatMap((e): { r2_key: string; time?: number; frame?: number }[] => {
    if (!e || typeof e !== 'object') return [];
    const k = e as Record<string, unknown>;
    if (typeof k.r2_key !== 'string' || !k.r2_key) return [];
    if (typeof k.time === 'number') return [{ r2_key: k.r2_key, time: k.time }];
    if (typeof k.frame === 'number') return [{ r2_key: k.r2_key, frame: k.frame }];
    return [];
  });
}

// The task the gateway infers from the active mode's attachments. Text-only
// is always t2va; the other modes fall back to t2va when nothing is attached
// (Generate stays enabled, the job just runs without media).
export function inferTask(opts: {
  mode: H3Mode;
  hasFrame: boolean;
  hasRef: boolean;
}): H3Task {
  if (opts.mode === 'refs' && opts.hasRef) return 'ref2va';
  if (opts.mode === 'frames' && opts.hasFrame) return 'fl2va';
  return 't2va';
}
